import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type { PropertyAdInProgressWorkspace } from '../../types/propertyAdEditor';
import type { ResponseType } from '../../types/responses';
import type { State } from '../../types/states';

import { Loader, MyButton } from '../uiComponents';
import type { AiListingEditorNavigationState } from './aiListingNavigation';
import { ExistingInProgressDialog } from './ExistingInProgressDialog';
import { PropertyAdCreationMethod } from './PropertyAdCreationMethod';
import { PropertyAdEditor } from './PropertyAdEditor';
import { TextInProgressDialog } from './TextInProgressDialog';
import { canUsePdfImport } from './pdfImportAccess';

type EntryState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retryAction: EntryRetryAction }
  | { status: 'existing'; workspace: PropertyAdInProgressWorkspace }
  | {
      status: 'existingText';
      workspace: PropertyAdInProgressWorkspace;
      isResetting: boolean;
      error?: string;
    }
  | { status: 'methodSelection' }
  | { status: 'editor'; workspace: PropertyAdInProgressWorkspace }
  | { status: 'textReady'; id: string }
  | { status: 'pdfReady' };

type EntryRetryAction = 'lookup' | 'create' | 'reset' | 'recover' | 'openAi';

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const getResponseErrorMessage = <T,>(response: ResponseType<T>, fallback: string): string =>
  response.message || fallback;

export const CreatePropertyAdV2 = ({
  aiNavigationState: navigationState,
}: {
  aiNavigationState?: AiListingEditorNavigationState | null;
}) => {
  const aiWorkspaceId =
    typeof navigationState?.aiAutofillWorkspaceId === 'string'
      ? navigationState.aiAutofillWorkspaceId
      : null;
  const [entryState, setEntryState] = useState<EntryState>({ status: 'methodSelection' });
  const user = useSelector(({ auth }: State) => auth.user);
  const isMountedRef = useRef(true);
  const requestInFlightRef = useRef(false);
  const entryMethodRef = useRef<'manual' | 'text'>('manual');

  const openWorkspace = (workspace: PropertyAdInProgressWorkspace) => {
    if (entryMethodRef.current === 'text') {
      setEntryState({ status: 'textReady', id: workspace.id });
    } else {
      setEntryState({ status: 'editor', workspace });
    }
  };

  const showExistingWorkspace = (workspace: PropertyAdInProgressWorkspace) => {
    if (entryMethodRef.current === 'text') {
      setEntryState({ status: 'existingText', workspace, isResetting: false });
    } else {
      setEntryState({ status: 'existing', workspace });
    }
  };

  const runRequest = useCallback(async (request: () => Promise<void>) => {
    if (requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    setEntryState({ status: 'loading' });

    try {
      await request();
    } finally {
      requestInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openAiWorkspace = useCallback(async () => {
    if (!aiWorkspaceId) return;

    try {
      const response = await propertyAdEditorProvider.getInProgressWorkspace();
      if (!isMountedRef.current) return;

      if (response.ok && response.data?.id === aiWorkspaceId) {
        setEntryState({ status: 'editor', workspace: response.data });
      } else {
        setEntryState({
          status: 'error',
          message: 'Unable to load the generated listing workspace',
          retryAction: 'openAi',
        });
      }
    } catch {
      if (isMountedRef.current) {
        setEntryState({
          status: 'error',
          message: 'Unable to load the generated listing workspace',
          retryAction: 'openAi',
        });
      }
    }
  }, [aiWorkspaceId]);

  useEffect(() => {
    if (aiWorkspaceId) void runRequest(openAiWorkspace);
  }, [aiWorkspaceId, openAiWorkspace, runRequest]);

  const recoverAfterCreateConflict = async () => {
    try {
      const response = await propertyAdEditorProvider.getInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        showExistingWorkspace(response.data);
        return;
      }

      setEntryState({
        status: 'error',
        message: getResponseErrorMessage(response, 'Unable to recover the listing workspace'),
        retryAction: 'recover',
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to recover the listing workspace'),
        retryAction: 'recover',
      });
    }
  };

  const createWorkspace = async () => {
    try {
      const response = await propertyAdEditorProvider.createInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        openWorkspace(response.data);
        return;
      }

      if (response.status === 409) {
        await recoverAfterCreateConflict();
        return;
      }

      setEntryState({
        status: 'error',
        message: getResponseErrorMessage(response, 'Unable to start the listing workspace'),
        retryAction: 'create',
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to start the listing workspace'),
        retryAction: 'create',
      });
    }
  };

  const loadWorkspace = async () => {
    try {
      const response = await propertyAdEditorProvider.getInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (!response.ok) {
        setEntryState({
          status: 'error',
          message: getResponseErrorMessage(response, 'Unable to load the listing workspace'),
          retryAction: 'lookup',
        });
        return;
      }

      if (response.data) {
        showExistingWorkspace(response.data);
        return;
      }

      await createWorkspace();
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to load the listing workspace'),
        retryAction: 'lookup',
      });
    }
  };

  const resetWorkspace = async () => {
    try {
      const response = await propertyAdEditorProvider.resetInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        setEntryState({ status: 'editor', workspace: response.data });
        return;
      }

      setEntryState({
        status: 'error',
        message: getResponseErrorMessage(response, 'Unable to reset the listing workspace'),
        retryAction: 'reset',
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to reset the listing workspace'),
        retryAction: 'reset',
      });
    }
  };

  const resetTextWorkspace = async (workspace: PropertyAdInProgressWorkspace) => {
    if (requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    setEntryState({ status: 'existingText', workspace, isResetting: true });

    try {
      const response = await propertyAdEditorProvider.resetInProgressWorkspace();
      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        openWorkspace(response.data);
      } else {
        setEntryState({
          status: 'existingText',
          workspace,
          isResetting: false,
          error: getResponseErrorMessage(response, 'Unable to reset the listing workspace'),
        });
      }
    } catch (error) {
      if (isMountedRef.current) {
        setEntryState({
          status: 'existingText',
          workspace,
          isResetting: false,
          error: getErrorMessage(error, 'Unable to reset the listing workspace'),
        });
      }
    } finally {
      requestInFlightRef.current = false;
    }
  };

  const handleSelectMethod = (method: 'manual' | 'text') => {
    if (requestInFlightRef.current) return;
    entryMethodRef.current = method;
    void runRequest(loadWorkspace);
  };

  const handleRetry = () => {
    if (entryState.status !== 'error') return;

    const retryRequest = {
      lookup: loadWorkspace,
      create: createWorkspace,
      reset: resetWorkspace,
      recover: recoverAfterCreateConflict,
      openAi: openAiWorkspace,
    }[entryState.retryAction];

    void runRequest(retryRequest);
  };

  if (entryState.status === 'loading') {
    return (
      <Stack
        alignItems='center'
        aria-label='Loading listing workspace'
        justifyContent='center'
        minHeight={280}
        padding='32px 16px'
        role='status'
        width='100%'
      >
        <Loader />
      </Stack>
    );
  }

  if (entryState.status === 'error') {
    return (
      <Stack
        alignItems='center'
        component='section'
        justifyContent='center'
        minHeight={280}
        padding='32px 16px'
        role='alert'
        textAlign='center'
        width='100%'
      >
        <Typography color='#2a3842' component='h1' fontSize={22} variant='h5'>
          {entryMethodRef.current === 'text'
            ? 'Unable to open listing workspace'
            : 'Unable to open PropertyAd Editor'}
        </Typography>
        <Typography
          color='#6f7d87'
          component='p'
          fontWeight={400}
          margin='10px 0 20px'
          maxWidth={520}
        >
          {entryState.message}
        </Typography>
        <MyButton
          data={{
            buttonName: 'Retry',
            buttonType: 'button',
            customWidth: '120px',
            variant: 'contained',
          }}
          onClick={handleRetry}
        />
      </Stack>
    );
  }

  if (entryState.status === 'methodSelection') {
    return (
      <PropertyAdCreationMethod
        onManual={() => handleSelectMethod('manual')}
        onText={() => handleSelectMethod('text')}
        onPdf={() => setEntryState({ status: 'pdfReady' })}
        pdfAvailable={canUsePdfImport(user)}
      />
    );
  }

  if (entryState.status === 'textReady') {
    return <Navigate replace to={`/ads/create/text/${encodeURIComponent(entryState.id)}`} />;
  }

  if (entryState.status === 'pdfReady') {
    return <Navigate replace to='/ads/create/pdf' />;
  }

  if (entryState.status === 'editor') {
    return (
      <PropertyAdEditor
        aiLocationStatus={
          aiWorkspaceId === entryState.workspace.id
            ? navigationState?.aiAutofillLocationStatus
            : undefined
        }
        aiReview={
          aiWorkspaceId === entryState.workspace.id ? navigationState?.aiAutofillReview : undefined
        }
        workspace={entryState.workspace}
      />
    );
  }

  if (entryState.status === 'existingText') {
    const { workspace, isResetting, error } = entryState;
    return (
      <Box minWidth={0} width='100%'>
        <TextInProgressDialog
          error={error}
          isResetting={isResetting}
          onCancel={() => setEntryState({ status: 'methodSelection' })}
          onClear={() => void resetTextWorkspace(workspace)}
          onContinue={() => setEntryState({ status: 'editor', workspace })}
        />
      </Box>
    );
  }

  return (
    <Box minWidth={0} width='100%'>
      <ExistingInProgressDialog
        onContinue={(workspace) => setEntryState({ status: 'editor', workspace })}
        onStartNew={() => void runRequest(resetWorkspace)}
        workspace={entryState.workspace}
      />
    </Box>
  );
};
